import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { VENUE_META, shortHash, timeAgo } from "@/lib/meta";
import { Flame, ExternalLink } from "lucide-react";

export default function JuiceBurns() {
  const [d, setD] = useState({ burns: [], juice_total: 0, token_total: 0 });
  const [filter, setFilter] = useState("all");
  useEffect(() => {
    api.get("/burns").then(({ data }) => setD(data)).catch(() => {});
  }, []);

  const total = d.juice_total + d.token_total;
  const juicePct = total ? (d.juice_total / total) * 100 : 50;
  const shown = d.burns.filter((b) => filter === "all" || b.kind === filter);

  return (
    <div className="mx-auto max-w-4xl px-5 py-8 lg:px-8">
      <div className="mb-1 flex items-center gap-2"><Flame size={20} className="text-pulp" /><h1 className="text-2xl font-bold tracking-tight">Moon Burns</h1></div>
      <p className="mb-6 text-sm text-dust">Every fee, buy and burn is public. It all feeds the burn.</p>

      {/* 50/50 breakdown */}
      <div className="rounded-2xl border border-line bg-surface p-5" data-testid="burns-breakdown">
        <div className="mb-3 flex items-center justify-between text-sm">
          <span className="text-dust">50 / 50 buy+burn split</span>
          <span className="font-mono text-cream">${total.toFixed(2)} total</span>
        </div>
        <div className="flex h-3 overflow-hidden rounded-full bg-raised">
          <div className="h-full bg-juice transition-all duration-500" style={{ width: `${juicePct}%` }} />
          <div className="h-full bg-pulp transition-all duration-500" style={{ width: `${100 - juicePct}%` }} />
        </div>
        <div className="mt-3 grid grid-cols-2 gap-3 font-mono text-sm">
          <div className="rounded-lg bg-raised/40 p-3"><p className="text-xs text-dust">$MOON burned</p><p className="text-juice">${d.juice_total.toFixed(2)}</p></div>
          <div className="rounded-lg bg-raised/40 p-3"><p className="text-xs text-dust">User tokens burned</p><p className="text-pulp">${d.token_total.toFixed(2)}</p></div>
        </div>
      </div>

      {/* filter */}
      <div className="mt-6 flex gap-2">
        {[["all", "All"], ["juice", "$MOON"], ["user", "User tokens"]].map(([k, lbl]) => (
          <button key={k} onClick={() => setFilter(k)} data-testid={`burn-filter-${k}`}
            className={`rounded-full border px-3 py-1.5 text-xs transition-colors ${filter === k ? "border-juice bg-juice/10 text-cream" : "border-line text-dust hover:bg-raised"}`}>{lbl}</button>
        ))}
      </div>

      {/* ledger */}
      <div className="mt-4 overflow-hidden rounded-2xl border border-line bg-surface" data-testid="burns-ledger">
        <div className="grid grid-cols-[1fr_1fr_1fr_1.4fr_0.7fr] gap-2 border-b border-line px-5 py-3 text-[11px] uppercase tracking-wider text-dust">
          <span>Token</span><span>Amount</span><span>Chain</span><span>Tx</span><span className="text-right">When</span>
        </div>
        {shown.map((b) => {
          const v = VENUE_META[b.venue] || {};
          return (
            <div key={b.id} className="grid grid-cols-[1fr_1fr_1fr_1.4fr_0.7fr] items-center gap-2 border-b border-line/60 px-5 py-3 font-mono text-sm transition-colors hover:bg-raised/40 animate-pulp-flash">
              <span className="flex items-center gap-2">
                <span className={b.kind === "juice" ? "text-juice" : "text-pulp"}>${b.token}</span>
                {b.source === "expired_creator_fees" && <span className="rounded bg-pulp/15 px-1.5 py-0.5 text-[9px] uppercase text-pulp">→ 4663 buy+burn</span>}
              </span>
              <span className="text-cream">{b.usdc_amount != null ? `${b.usdc_amount.toFixed(2)} USDC` : `$${b.amount_usd.toFixed(2)}`}</span>
              <span className="text-dust">{b.chain}</span>
              <a href={`${b.explorer ? b.explorer : `https://${v.explorer || "solscan.io"}`}/tx/${b.tx_hash}`} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-ai hover:underline">
                {shortHash(b.tx_hash)} <ExternalLink size={11} />
                {b.simulated && <span className="rounded bg-ai/15 px-1.5 py-0.5 text-[9px] uppercase text-ai">sim</span>}
              </a>
              <span className="text-right text-xs text-dust">{timeAgo(b.created_at)}</span>
            </div>
          );
        })}
        {!shown.length && <p className="p-5 text-sm text-dust">No burns yet.</p>}
      </div>
    </div>
  );
}
